/**
 * Process Detail Editor
 * 
 * Reusable component for editing process details with tabbed content
 * Can be used in tabs, cards, or dialogs
 */

import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Card, message, Button, Switch } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { apiClient } from '../../../services/apiClient';
import { ProcessType, DemandFlowType } from '../../../types/enums';
import type { PrimeProcess } from '../../../types/primeModel';
import { ProcessArtifactManager } from '../ProcessArtifactManager';
import {
  ProcessParametersEditor,
  DemandCalculationRulesEditor,
  ValidationRulesEditor,
  ImprovementParametersEditor
} from '../../Parameters';
import {
  ProcessParameters,
  DemandCalculationRules,
  ImprovementParameters,
  DurationBehaviorConfig
} from '../../../types/processParameters';
import { ValidationRules, createParameterDefinition } from '../../../types/transitionParameters';
import { DURATION_BEHAVIOR_CATALOG } from '../../../config/processDurationCatalog';
import './EditorStyles.css';

const { TextArea } = Input;

interface ProcessDetailEditorProps {
  processId: string;
  artifactId: string;
  activeTab?: string; // Which tab content to show
  onSave?: () => void;
  embedded?: boolean;
  onChange?: (values: any) => void;
}

export const ProcessDetailEditor: React.FC<ProcessDetailEditorProps> = ({
  processId,
  artifactId,
  activeTab = 'basic',
  onSave,
  embedded = false,
  onChange
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [processData, setProcessData] = useState<PrimeProcess | null>(null);
  const [processParams, setProcessParams] = useState<ProcessParameters>({});
  const [demandCalculationRules, setDemandCalculationRules] = useState<DemandCalculationRules>({});
  const [validationRules, setValidationRules] = useState<ValidationRules>({});
  const [improvementParams, setImprovementParams] = useState<ImprovementParameters>({});
  const [artifactName, setArtifactName] = useState<string>('');
  const [transitions, setTransitions] = useState<any[]>([]);

  useEffect(() => {
    loadProcessData();
  }, [processId]);

  const loadProcessData = async () => {
    try {
      setLoading(true);
      
      // Load process, artifact, and transitions in parallel
      const [processRes, artifactRes, transitionsRes] = await Promise.all([
        apiClient.get(`/prime-processes/${processId}`),
        apiClient.get(`/prime-artifacts/${artifactId}`),
        apiClient.get(`/prime-processes/${processId}/transitions`)
      ]);
      
      setProcessData(processRes.data);
      setArtifactName(artifactRes.data.name);
      setTransitions(transitionsRes.data);
      
      // Auto-generate name based on artifact and transitions
      let autoGeneratedName = processRes.data.name;
      if (transitionsRes.data && transitionsRes.data.length > 0) {
        // Use first transition name for process naming
        const firstTransition = transitionsRes.data[0];
        autoGeneratedName = `${artifactRes.data.name} - ${firstTransition.name}`;
      }
      
      form.setFieldsValue({
        name: autoGeneratedName,
        description: processRes.data.description || '',
        process_type: processRes.data.process_type,
        demand_flow_type: processRes.data.demand_flow_type,
      });
      
      setProcessParams(processRes.data.process_parameters || {});
      setDemandCalculationRules(processRes.data.demand_calculation_rules || {});
      setValidationRules(processRes.data.validation_rules || {});
      setImprovementParams(processRes.data.improvement_parameters || {});
    } catch (error) {
      console.error('Error loading process data:', error);
      message.error('Failed to load process data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload = {
        name: values.name,
        description: values.description,
        prime_artifact_ref: artifactId,
        process_type: values.process_type,
        demand_flow_type: values.demand_flow_type,
        process_parameters: processParams,
        demand_calculation_rules: demandCalculationRules,
        validation_rules: validationRules,
        improvement_parameters: improvementParams,
      };

      await apiClient.put(`/prime-processes/${processId}`, payload);
      message.success('Process updated successfully');
      onSave?.();
    } catch (error: any) {
      console.error('Error saving process:', error);
      message.error(`Failed to save process: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleFormChange = () => {
    if (embedded && onChange) {
      const values = form.getFieldsValue();
      onChange({ ...values, process_parameters: processParams });
    }
  };

  if (loading) {
    return <div className="editor-loading">Loading process data...</div>;
  }

  // Render content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'process-basic':
      case 'basic':
        return (
          <Form
            form={form}
            layout="vertical"
            onValuesChange={handleFormChange}
          >
            <Form.Item
              label="Process Name (Auto-Generated)"
              name="name"
              tooltip="Name is automatically generated based on artifact and transition"
            >
              <Input disabled style={{ backgroundColor: '#f5f5f5', color: '#000' }} />
            </Form.Item>

            <Form.Item
              label="Description"
              name="description"
            >
              <TextArea rows={3} placeholder="Enter process description" />
            </Form.Item>

            <Form.Item
              label="Process Type"
              name="process_type"
              rules={[{ required: true, message: 'Please select a process type' }]}
            >
              <Select placeholder="Select process type">
                <Select.Option value="DEFAULT">Default</Select.Option>
                <Select.Option value="DEMAND">Demand</Select.Option>
                <Select.Option value="SUPPLY">Supply</Select.Option>
                <Select.Option value="BUFFER">Buffer</Select.Option>
                <Select.Option value="THROUGHPUT">Throughput</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="Demand Flow Type"
              name="demand_flow_type"
            >
              <Select placeholder="Select demand flow type">
                <Select.Option value="PUSH">Push</Select.Option>
                <Select.Option value="PULL">Pull</Select.Option>
                <Select.Option value="DRUM">Drum</Select.Option>
              </Select>
            </Form.Item>
          </Form>
        );
      
      case 'process-duration':
      case 'duration':
        return (
          <div className="editor-section">
            <div className="editor-section-title">Duration Behavior Configuration</div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Duration Behavior Type:</label>
              <Select
                style={{ width: '100%' }}
                placeholder="Select duration behavior type"
                value={processParams.duration_behavior_type?.value}
                onChange={(value) => {
                  setProcessParams({
                    ...processParams,
                    duration_behavior_type: {
                      type: 'string' as const,
                      value: value,
                      description: 'Duration behavior type'
                    }
                  });
                }}
              >
                {Object.entries(DURATION_BEHAVIOR_CATALOG).map(([key, config]) => (
                  <Select.Option key={key} value={key}>
                    {config.label} - {config.description}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <p style={{ fontSize: 12, color: '#6b7280', fontStyle: 'italic' }}>
              Configure how this process determines its duration and execution timing
            </p>
          </div>
        );
      
      case 'process-artifacts-detail':
      case 'artifacts':
        return (
          <ProcessArtifactManager
            processId={processId}
            processName={processData?.name || 'Process'}
            onUpdate={() => {
              // Reload process data if needed
              loadProcessData();
            }}
          />
        );
      
      case 'process-parameters':
      case 'parameters':
        return (
          <ProcessParametersEditor
            parameters={processParams}
            onChange={setProcessParams}
          />
        );
      
      case 'process-demand':
      case 'demand':
        return (
          <DemandCalculationRulesEditor
            rules={demandCalculationRules}
            onChange={setDemandCalculationRules}
          />
        );
      
      case 'process-validation':
      case 'validation':
        return (
          <ValidationRulesEditor
            validationRules={validationRules}
            onChange={setValidationRules}
          />
        );
      
      case 'process-improvement':
      case 'improvement':
        return (
          <ImprovementParametersEditor
            improvements={improvementParams}
            onChange={setImprovementParams}
          />
        );
      
      default:
        return <div>Select a tab to view content</div>;
    }
  };

  return (
    <div className="process-detail-editor">
      <Card
        title={`Process: ${processData?.name || 'Unknown'}`}
        className="editor-card"
        extra={
          !embedded && (
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
            >
              Save Changes
            </Button>
          )
        }
      >
        {renderTabContent()}
      </Card>
    </div>
  );
};

